/**
 * Product recipes (materials): pure decision logic.
 *
 * A sellable product may carry a RECIPE: the materials one sold unit of it uses
 * up (a milkshake takes 1 cup, 1 lid, 1 straw). Materials are stock items that
 * are never sold (`products.is_material`); they exist to be consumed and costed.
 *
 * Two rules live here so the API, its tests and any future preview agree:
 *
 *   1. WHICH rows apply to a sold line (`resolveRecipe`): a variation with rows
 *      of its own uses only those, otherwise the product-level rows.
 *   2. HOW MUCH is taken (`planMaterialUse`): a material never blocks a sale. It
 *      runs down to zero and stops there, and the part that could not be taken
 *      is reported as `short` so the shelf count can be corrected later.
 *
 * This module is pure (no DB). The DB-aware wrapper lives in
 * `apps/api/src/services/product-materials.ts`.
 */

export type RecipeLine = {
  /**
   * NULL/absent = the product-level recipe. Set = the recipe of one specific
   * VARIATION of the sold product.
   */
  variantId?: string | null;
  materialProductId: string;
  /** Units of the material (in the material's own unit) used per sold unit. */
  qtyPerUnit: number;
};

/**
 * The recipe rows that apply to one sold line.
 *
 * A variation that has ANY rows of its own uses only those: a replace, never a
 * merge. Merging would make "the Large uses a different cup" impossible to
 * express, because the Large would also inherit the default cup. A variation
 * with no rows, and a product sold without a variation, use the product-level
 * rows.
 */
export function resolveRecipe(lines: RecipeLine[], variantId?: string | null): RecipeLine[] {
  if (variantId) {
    const own = lines.filter((l) => l.variantId === variantId);
    if (own.length > 0) return own;
  }
  return lines.filter((l) => !l.variantId);
}

export type MaterialUse = {
  materialProductId: string;
  /** What the recipe calls for: `qtyPerUnit × units sold`. */
  required: number;
  /** What is actually taken off the shelf: `min(required, on hand)`. */
  take: number;
  /** `required − take`. Above zero means the shelf count was already wrong. */
  short: number;
};

/**
 * How much of each material a sale of `qty` units uses, clamped to what the
 * branch holds.
 *
 * `stock` is keyed by material product id; a missing key reads as zero. The
 * result is one entry per distinct material, in ascending id order so callers
 * that lock rows take them in a stable order. Rows naming the same material are
 * summed before the clamp, so two lines can never both claim the last unit.
 */
export function planMaterialUse(args: {
  recipe: RecipeLine[];
  qty: number;
  stock: ReadonlyMap<string, number>;
}): MaterialUse[] {
  if (!Number.isInteger(args.qty) || args.qty <= 0) return [];
  const required = new Map<string, number>();
  for (const line of args.recipe) {
    if (!Number.isInteger(line.qtyPerUnit) || line.qtyPerUnit <= 0) continue;
    required.set(
      line.materialProductId,
      (required.get(line.materialProductId) ?? 0) + line.qtyPerUnit * args.qty,
    );
  }
  return [...required.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([materialProductId, need]) => {
      const onHand = Math.max(0, args.stock.get(materialProductId) ?? 0);
      const take = Math.min(need, onHand);
      return { materialProductId, required: need, take, short: need - take };
    });
}

/**
 * The marker a material movement carries in `inventory_movements.note`.
 *
 * `sold_*` names the ORDER LINE the movement belongs to, which is what a
 * per-line restock keys on. `recipe_of` names the product whose recipe called
 * for the material: the sold product itself, or one of its bundle inclusions (a
 * combo's drink brings its own cup).
 */
export type MaterialMovementNote = {
  material: true;
  sold_product_id: string;
  sold_variant_id: string | null;
  /**
   * The sold product's name AT THE TIME, so the stock log reads on its own: a
   * log row must not turn into a bare id when the product is later renamed or
   * archived, and the three places that render notes should not each need a
   * product lookup.
   */
  sold_name: string | null;
  recipe_of: string;
  required: number;
  short: number;
  /** Set on the `material_restock` twin that puts a consumption back. */
  restock: boolean;
  /** The void/refund reason carried by a restock movement, if any. */
  reason: string | null;
};

export type MaterialNoteInput = {
  sold_product_id: string;
  sold_variant_id: string | null;
  sold_name?: string | null;
  recipe_of: string;
  required?: number;
  short?: number;
  restock?: boolean;
  reason?: string | null;
};

export function encodeMaterialNote(note: MaterialNoteInput): string {
  return JSON.stringify({
    material: true,
    sold_product_id: note.sold_product_id,
    sold_variant_id: note.sold_variant_id,
    sold_name: note.sold_name ?? null,
    recipe_of: note.recipe_of,
    required: note.required ?? 0,
    short: note.short ?? 0,
    ...(note.restock ? { restock: true, reason: note.reason ?? null } : {}),
  });
}

/** Parse a movement note back into its marker, or null when it is not one. */
export function decodeMaterialNote(note: string | null | undefined): MaterialMovementNote | null {
  if (!note) return null;
  const trimmed = note.trim();
  if (!trimmed.startsWith('{')) return null;
  try {
    const parsed = JSON.parse(trimmed) as Record<string, unknown>;
    if (parsed.material !== true || typeof parsed.sold_product_id !== 'string') return null;
    return {
      material: true,
      sold_product_id: parsed.sold_product_id,
      sold_variant_id: typeof parsed.sold_variant_id === 'string' ? parsed.sold_variant_id : null,
      sold_name: typeof parsed.sold_name === 'string' ? parsed.sold_name : null,
      recipe_of: typeof parsed.recipe_of === 'string' ? parsed.recipe_of : parsed.sold_product_id,
      required: typeof parsed.required === 'number' ? parsed.required : 0,
      short: typeof parsed.short === 'number' ? parsed.short : 0,
      restock: parsed.restock === true,
      reason: typeof parsed.reason === 'string' ? parsed.reason : null,
    };
  } catch {
    return null;
  }
}
